// START IMPORTS
import { CustomIcons, useTheme } from '@ltvco/refresh-lib/theme';
import { getVisibleItems } from '@ltvco/refresh-lib/utils';
import { Owner } from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Email,
  Radar,
  Person2,
  Phone,
  Work,
  TaskAlt,
} from '@mui/icons-material';

import {
  getAssociatedAccountsStandaloneNavItem,
  getAssociatedAccountsStandaloneNavLinkData,
} from './phoneNavLinkData';
// END IMPORTS

// START HELPER_FUNCTIONS
type IContactItemsForCount = {
  phones: Owner['phones'];
  emails: Owner['emails'];
  addresses: Owner['addresses'];
};

export const getEmailNavLinkData = (
  potentialOwnersLength: number,
  owner: Owner,
  contactItemsForCount?: IContactItemsForCount,
  associatedAccountsCount?: number | null,
  isAssociatedAccountsCountLoading?: boolean
) => {
  const contactInfoItems = contactItemsForCount ?? {
    phones: owner.phones,
    emails: owner.emails,
    addresses: owner.addresses,
  };
  const visiblePhoneCount = getVisibleItems(contactInfoItems.phones).length;
  const visibleEmailCount = getVisibleItems(contactInfoItems.emails).length;
  const visibleAddressCount = getVisibleItems(
    contactInfoItems.addresses
  ).length;
  const isEmailPartialAddress = useFeatureIsOn('OAR-1706');
  const isInAssociatedAccountsSectionVariation = useFeatureIsOn('RFRSH-4465');

  return [
    {
      href: '#email-overview',
      sectionId: '#email-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    {
      href: '#email-fraud-check-section',
      sectionId: '#email-fraud-check-section"',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'fraud_check',
    },
    ...(isInAssociatedAccountsSectionVariation
      ? [
          getAssociatedAccountsStandaloneNavLinkData(
            associatedAccountsCount,
            isAssociatedAccountsCountLoading
          ),
        ]
      : []),
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: potentialOwnersLength,
      notificationSection: 'owners',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Phone Numbers',
      reportItemsCount: Math.max(visiblePhoneCount - 1, 0),
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Emails',
      reportItemsCount: visibleEmailCount,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isEmailPartialAddress ? 'Possible Locations' : 'Address History',
      reportItemsCount: visibleAddressCount,
      notificationSection: 'addresses',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Professional Background',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Education',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social & Websites',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount:
        (owner?.ownedAssets?.real_estate?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.watercraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.aircraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.automobiles?.length ?? 0),
      notificationSection: 'real_estate',
    },
  ];
};

export const getEmailNavLinkDataV2 = (
  potentialOwnersLength: number,
  owner: Owner,
  contactItemsForCount?: IContactItemsForCount,
  associatedAccountsCount?: number | null,
  isAssociatedAccountsCountLoading?: boolean
) => {
  const theme = useTheme();
  const isEmailPartialAddress = useFeatureIsOn('OAR-1706');
  const contactInfoItems = contactItemsForCount ?? {
    phones: owner.phones,
    emails: owner.emails,
    addresses: owner.addresses,
  };
  const visiblePhoneCount = getVisibleItems(contactInfoItems.phones).length;
  const visibleEmailCount = getVisibleItems(contactInfoItems.emails).length;
  const visibleAddressCount = getVisibleItems(
    contactInfoItems.addresses
  ).length;
  const isInAssociatedAccountsSectionVariation = useFeatureIsOn('RFRSH-4465');

  let potentialOwnerNumbers = 0;

  if (potentialOwnersLength > 0) {
    potentialOwnerNumbers = potentialOwnersLength;
  }

  return [
    {
      href: '#email-overview',
      labelText: 'Overview',
      name: 'email-overview',
      icons: [
        <Email
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
        />,
      ],
      notificationSection: 'identity',
    },
    {
      href: '#email-fraud-check-section',
      labelText: 'FraudScan Summary',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      hideReportItemsCount: true,
      name: 'email-fraud-check-section',
      notificationSection: 'fraud_check',
    },
    ...(isInAssociatedAccountsSectionVariation
      ? [
          getAssociatedAccountsStandaloneNavItem(
            theme,
            associatedAccountsCount,
            isAssociatedAccountsCountLoading
          ),
        ]
      : []),
    {
      labelText: 'Data Breaches',
      name: 'dark-web-email-section',
      showIcon: true,
      hideReportItemsCount: true,
      href: '#dark-web-email-section',
      icons: [
        <CustomIcons.DarkWebIcon
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
    },
    {
      labelText: 'Potential Owners',
      name: 'potential-owners-section',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: potentialOwnerNumbers,
          name: 'potential-owners-section',
          notificationSection: 'owners',
        },
        {
          href: '#possible-photos-section',
          labelText: 'Possible Photos',
          reportItemsCount: owner.images?.length || null,
          name: 'possible-photos-section',
          notificationSection: 'photos',
        },
      ],
    },
    {
      labelText: 'Contact Information',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(visiblePhoneCount - 1, 0),
          name: 'phone-numbers-section',
          notificationSection: 'phones',
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: visibleEmailCount,
          name: 'email-section',
          notificationSection: 'emails',
        },
        {
          href: '#address-history-section',
          labelText: isEmailPartialAddress
            ? 'Possible Locations'
            : 'Address History',
          reportItemsCount: visibleAddressCount,
          name: 'address-history-section',
          notificationSection: 'addresses',
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
          name: 'social-media-section',
          notificationSection: 'social',
        },
      ],
    },
    {
      labelText: 'Jobs & Education',
      name: 'jobs-educations-section',
      icons: [
        <Work sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />,
      ],
      childItems: [
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
          name: 'jobs-section',
          notificationSection: 'jobs',
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
          name: 'education-section',
          notificationSection: 'educations',
        },
      ],
    },
    {
      labelText: 'Assets',
      icons: [<CustomIcons.OwnedAssetsIcon color="primary" fontSize="small" />],
      href: '#possible-owned-assets-section',
      name: 'assets',
      reportItemsCount:
        (owner?.ownedAssets?.real_estate?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.watercraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.aircraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.automobiles?.length ?? 0),
      notificationSection: ['real_estate', 'automobiles', 'vehicles'],
    },
    {
      labelText: 'Next Steps',
      name: 'next-steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#attachments-section',
          labelText: 'Report Attachments',
          hideReportItemsCount: true,
          name: 'attachments',
          visible: false,
        },
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
